import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import axios from 'axios'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken(apiToken)
          if (data) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.api_token) {
      requestUser(auth.api_token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

// Nuevo componente para manejar el interceptor de Axios
// Este debe ser usado DENTRO del Router
const AxiosInterceptor: FC<WithChildren> = ({children}) => {
  const {logout} = useAuth()

  // NUEVO: Estado para controlar la visibilidad de la alerta y el contador
  const [isSessionExpired, setIsSessionExpired] = useState(false)
  const [countdown, setCountdown] = useState(5)

  // NUEVO: Efecto para manejar el contador regresivo
  useEffect(() => {
    let timer: any

    if (isSessionExpired) {
      if (countdown > 0) {
        // Disminuir contador cada segundo
        timer = setTimeout(() => {
          setCountdown((prev) => prev - 1)
        }, 1000)
      } else {
        // Cuando llega a 0, ejecutar logout y redirección
        logout()
        window.location.href = '/app/auth'
      }
    }

    return () => clearTimeout(timer)
  }, [isSessionExpired, countdown, logout])

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          const {status, data} = error.response
          if (status === 401) {
            const isTokenExpired =
              data?.msg === 'Token no valido' ||
              data?.error?.name === 'TokenExpiredError' ||
              data?.error?.message === 'jwt expired'

            if (isTokenExpired) {
              // MODIFICADO: En lugar de logout directo, activamos el estado
              setIsSessionExpired(true)
            }
          }
        }
        return Promise.reject(error)
      }
    )

    return () => {
      axios.interceptors.response.eject(interceptor)
    }
  }, []) // Removemos 'logout' de dependencias para evitar recrear el interceptor innecesariamente

  return (
    <>
      {children}

      {/* NUEVO: Modal/Overlay simple que se muestra cuando la sesión expira */}
      {isSessionExpired && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            color: '#fff',
            flexDirection: 'column',
            fontFamily: 'inherit',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              color: '#333',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              minWidth: '300px',
            }}
          >
            <h2 style={{marginTop: 0, color: '#f1416c'}}>Sesión Expirada</h2>
            <p style={{fontSize: '1.1rem', marginBottom: '1.5rem'}}>
              Tu sesión ha expirado, se te redirigirá al login.
            </p>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#009ef7'}}>{countdown}</div>
          </div>
        </div>
      )}
    </>
  )
}

export {AuthProvider, AuthInit, useAuth, AxiosInterceptor}
